import * as Yup from "yup";
import { isString, typedKeys } from "../validations";
import constant from "../constant";

const { COUNTRIES, INDUSTRIES, BUSINESS } = constant;

const countryCodes = typedKeys(COUNTRIES);
const industryCodes = INDUSTRIES.map(i => i.code);

const media = Yup.object({
    path: Yup.string().required(),
    text: Yup.string().default(""),
    variants: Yup.array()
        .of(
            Yup.object({
                type: Yup.string()
                    .oneOf(["highres", "large", "medium", "small", "thumbnail"])
                    .default("highres"),
                url: Yup.string().url("Must be a valid URL").required("URL is required"),
            }),
        )
});

export default {
    login: Yup.object({
        email: Yup.string().email("Invalid email").required("Email is required"),
        password: Yup.string()
            .required("Password is required")
            .min(8, "Must be at least 8 characters"),
        remember: Yup.boolean().default(true),
    }).required(),
    resetLink: Yup.object({
        email: Yup.string().email("Invalid email").required("Email is required"),
    }),
    register: Yup.object({
        email: Yup.string().email("Invalid email").required("Email is required"),
        password: Yup.string()
            .required("Password is required")
            .min(8, "Must be at least 8 characters"),
        confirmPassword: Yup.string()
            .required("Password is required")
            .test("passwords-match", "Passwords don't match", function (value) {
                return value === this.parent.password || !this.parent.password;
            }),
    }),
    resetPassword: Yup.object({
        newPassword: Yup.string()
            .required("Password is required")
            .min(8, "Must be at least 8 characters"),
        confirmPassword: Yup.string()
            .required("Password is required")
            .test("passwords-match", "Passwords don't match", function (value) {
                return value === this.parent.newPassword || !this.parent.newPassword;
            }),
    }),

    profile: Yup.object({
        email: Yup.string().email().required("Email is required"),

        currentPassword: Yup.string().when({
            is: (value: string) => isString(value),
            then: s => s.min(8, "Must be at least 8 characters"),
        }),
        newPassword: Yup.string()
            .when("currentPassword", {
                is: (value: string) => isString(value),
                then: s => s.required("New password is required"),
            })
            .when({
                is: (value: string) => isString(value),
                then: s =>
                    s
                        .min(8, "Must be at least 8 characters")
                        .matches(
                            /^(?=.*[~`!@#$%^&*()-_+={}[\]|\\:;"<>,./?])/,
                            "Password must contain at least one special character",
                        )
                        .test(
                            "different-password",
                            "New password must be different from the current password",
                            function (value) {
                                return value !== this.parent.currentPassword;
                            },
                        ),
            }),
        repeatNewPassword: Yup.string()
            .when("newPassword", {
                is: (value: string) => isString(value),
                then: t => t.required("Repeat password is required"),
            })
            .test("passwords-match", "Passwords don't match", function (value) {
                return value === this.parent.newPassword || !this.parent.newPassword;
            }),

        password: Yup.string().when({
            is: (value: string) => isString(value),
            then: s =>
                s
                    .min(8, "Must be at least 8 characters")
                    .matches(
                        /^(?=.*[~`!@#$%^&*()-_+={}[\]|\\:;"<>,./?])/,
                        "Password must contain at least one special character",
                    ),
        }),
        repeatPassword: Yup.string()
            .when("password", {
                is: (value: string) => isString(value),
                then: s => s.required("Repeat password is required"),
            })
            .test("passwords-match", "Passwords don't match", function (value) {
                return value === this.parent.password || !this.parent.password;
            }),
    }),

    brand: Yup.object({
        title: Yup.string().required("Name is required").max(50, "Max 50 characters"),
        tag: Yup.string()
            .required("Tag is required")
            .max(50, "Must be 6 to 50 characters in length")
            .min(6, "Must be 6 to 50 characters in length")
            .matches(
                /^(?!.*\/\/)[a-z][a-z0-9-]{5,49}(?<!-)$/,
                "Can only contain lowercase letters, numbers and hyphen. Must start with letter. Can't end with hyphen",
            ),
        description: Yup.string().optional().max(5000, "Max 5000 characters"),
        media: media.optional().default(undefined),
        email: Yup.string().email("Invalid email").optional(),
        websiteURL: Yup.string().url("Must start with http:// or https://").optional(),
        socials: Yup.array()
            .of(Yup.string().required("Social link is required").url("Must be a valid URL"))
            .optional(),
        entities: Yup.array()
            .of(
                Yup.object({
                    business: Yup.string().required("Line of business is required").oneOf(BUSINESS),
                    title: Yup.string().required("Title is required").max(50, "Max 50 characters"),
                    countries: Yup.array()
                        .of(
                            Yup.string()
                                .required("Country is required")
                                .oneOf(typedKeys({ ...COUNTRIES, ALL: "All countries" })),
                        )
                        .min(1, "Select at least one country")
                        .required("Select at least one country"),
                    websiteURL: Yup.string().url("Must start with http:// or https://"),
                }),
            )
            .optional(),
    }),

    settings: Yup.object({
        title: Yup.string().required("Name is required").max(50, "Max 50 characters"),
        industry: Yup.string().required("Industry is required").oneOf(industryCodes),
        business: Yup.string().required("Line of business is required").oneOf(BUSINESS),
        companyId: Yup.string()
            .required("Company ID is required")
            .matches(/^[a-zA-Z0-9_-]+$/, "Invalid company ID")
            .max(18, "Max 18 characters"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        phone: Yup.string()
            .required("Phone is required")
            .matches(/^[0-9]+$/, "Invalid phone")
            .max(18, "Max 18 characters"),
        address: Yup.object({
            country: Yup.string().required("Country is required").oneOf(countryCodes),
            city: Yup.string()
                .required("City is required")
                .matches(/^[a-zA-Z\sÅÄÖåäöÜüØøÆæ\W]+$/i, {
                    message: "Invalid city",
                })
                .max(24, "Max 24 characters"),
            street: Yup.string().max(36, "Max 36 characters").required("Street is requied"),
            zipCode: Yup.string().max(18, "Max 18 characters"),
        }),
    }),

    organization: {
        step1: Yup.object({
            industry: Yup.string().required("Industry is required").oneOf(industryCodes),
            country: Yup.string().required("Country is required").oneOf(countryCodes),
            business: Yup.string().required("Line of business is required").oneOf(BUSINESS),
            companyId: Yup.string()
                .required("Company ID is required")
                .matches(/^[a-zA-Z0-9_-]+$/, "Invalid company ID")
                .max(18, "Max 18 characters"),
            title: Yup.string().required("Name is required").max(50, "Max 50 characters"),
        }),
        step2: Yup.object({
            email: Yup.string().email("Invalid email").required("Email is required"),
            phone: Yup.string()
                .required("Phone is required")
                .matches(/^[0-9]+$/, "Invalid phone")
                .max(18, "Max 18 characters"),
            street: Yup.string().max(36, "Max 36 characters").required("Street is required"),
            city: Yup.string()
                .required("City is required")
                .matches(/^[a-zA-Z\sÅÄÖåäöÜüØøÆæ\W]+$/i, {
                    message: "Invalid city",
                })
                .max(24, "Max 24 characters"),
            zipCode: Yup.string().max(18, "Max 18 characters"),
        }),
    },
};
